import { useEffect, useRef, useState } from "react";
import { CheckCircle2, MapPin, Search } from "lucide-react";
import { apiRequest, jsonBody } from "../lib/api";
import { Button, Notice } from "./ui";

type Suggestion = {
  id: string;
  formattedAddress: string;
  context: string;
  latitude: number;
  longitude: number;
  source: "MOCK" | "GEOAPIFY" | "LOCATIONIQ" | "GOOGLE";
};

type ConfirmedOrigin = {
  origin_address?: string;
  origin_normalized_address?: string;
  origin_latitude?: number;
  origin_longitude?: number;
  origin_confirmation_status?: string;
  config_version?: number;
};

export function OwnerOriginEditor({
  branchId,
  address,
  normalizedAddress,
  latitude,
  longitude,
  status,
  onConfirmed,
}: {
  branchId: string;
  address: string;
  normalizedAddress: string;
  latitude: number | null;
  longitude: number | null;
  status: "UNVERIFIED" | "SELECTED" | "CONFIRMED";
  onConfirmed: (origin: ConfirmedOrigin) => void;
}) {
  const [typedAddress, setTypedAddress] = useState(address);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selected, setSelected] = useState<Suggestion>();
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [manualLatitude, setManualLatitude] = useState(latitude?.toString() ?? "");
  const [manualLongitude, setManualLongitude] = useState(longitude?.toString() ?? "");
  const lastQuery = useRef("");

  useEffect(() => {
    if (!dirty || typedAddress.trim().length < 5) {
      return;
    }
    const query = typedAddress.trim();
    if (query === lastQuery.current) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      lastQuery.current = query;
      void apiRequest<{ mode: string; items: Suggestion[] }>(
        `/api/owner/branches/${encodeURIComponent(branchId)}/shipping-origin/search?q=${encodeURIComponent(query)}`,
        { signal: controller.signal },
      ).then((result) => {
        if (!controller.signal.aborted) {
          setSuggestions(result.items);
          setNotice(
            result.mode === "LOCAL_MOCK"
              ? "LOCAL / MOCK — koordinat ini bukan lokasi production."
              : result.items.length === 0
                ? "Alamat tidak ditemukan. Periksa penulisan atau gunakan koordinat manual yang sudah diverifikasi."
                : undefined,
          );
        }
      }).catch((error: unknown) => {
        if (!controller.signal.aborted) setNotice(error instanceof Error ? error.message : "Alamat belum dapat dicari.");
      });
    }, 450);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [branchId, dirty, typedAddress]);

  const confirm = async (manual: boolean) => {
    const lat = manual ? Number(manualLatitude) : selected?.latitude;
    const lng = manual ? Number(manualLongitude) : selected?.longitude;
    const normalized = manual ? typedAddress.trim() : selected?.formattedAddress;
    if (!normalized || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      setNotice("Pilih alamat atau isi koordinat manual yang sudah diverifikasi.");
      return;
    }
    setBusy(true);
    setNotice(undefined);
    try {
      const result = await apiRequest<ConfirmedOrigin>(
        `/api/owner/branches/${encodeURIComponent(branchId)}/shipping-origin/confirm`,
        {
          method: "POST",
          ...jsonBody({
            typedAddress: typedAddress.trim(),
            normalizedAddress: normalized,
            latitude: lat,
            longitude: lng,
            source: manual ? "MANUAL" : selected!.source,
            provider: manual ? null : selected!.source,
            placeId: manual ? null : selected!.id,
            confirmation: "CONFIRM_BRANCH_ORIGIN",
          }),
        },
      );
      onConfirmed(result);
      setTypedAddress(result.origin_normalized_address ?? normalized);
      setSelected(undefined);
      setSuggestions([]);
      setDirty(false);
      setNotice("Origin cabang sudah dikonfirmasi. Shipping V2 tetap OFF.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Origin belum berhasil dikonfirmasi.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="origin-editor" aria-label="Dispatch origin">
      <div className="origin-editor__heading">
        <div><strong>Dispatch origin</strong><small>Alamat harus dipilih dan dikonfirmasi; mengetik saja tidak menyimpan origin.</small></div>
        <span className={`origin-status origin-status--${status.toLowerCase()}`}><MapPin />{status}</span>
      </div>
      {status === "CONFIRMED" && !dirty && (
        <Notice>Origin tersimpan: {normalizedAddress || address} ({latitude}, {longitude}).</Notice>
      )}
      <label>
        Cari alamat origin
        <div className="origin-search-input"><Search /><input value={typedAddress} minLength={5} maxLength={500} placeholder="Ketik minimal 5 karakter" onChange={(event) => { setTypedAddress(event.target.value); setDirty(true); setSelected(undefined); setSuggestions([]); setNotice(undefined); }} /></div>
      </label>
      {notice && <Notice>{notice}</Notice>}
      {suggestions.length > 0 && (
        <div className="origin-suggestions" role="listbox" aria-label="Pilihan alamat">
          {suggestions.map((item) => (
            <button type="button" role="option" aria-selected={selected?.id === item.id} className={selected?.id === item.id ? "is-selected" : ""} key={item.id} onClick={() => { setSelected(item); setManualLatitude(String(item.latitude)); setManualLongitude(String(item.longitude)); }}>
              <strong>{item.formattedAddress}</strong><small>{item.context}</small>
            </button>
          ))}
        </div>
      )}
      {selected && <Button type="button" busy={busy} icon={<CheckCircle2 />} onClick={() => void confirm(false)}>Gunakan dan Konfirmasi Alamat Ini</Button>}
      <details className="origin-manual">
        <summary>Advanced / Manual Coordinates</summary>
        <p className="muted-copy">Gunakan hanya koordinat yang sudah diverifikasi. Validasi server dan konfirmasi tetap wajib.</p>
        <div className="two-field-grid">
          <label>Latitude<input type="number" min="-90" max="90" step="any" value={manualLatitude} onChange={(event) => setManualLatitude(event.target.value)} /></label>
          <label>Longitude<input type="number" min="-180" max="180" step="any" value={manualLongitude} onChange={(event) => setManualLongitude(event.target.value)} /></label>
        </div>
        <Button type="button" busy={busy} variant="secondary" onClick={() => void confirm(true)}>Konfirmasi Koordinat Manual</Button>
      </details>
      <small>Peta visual belum ditambahkan agar dashboard tetap ringan. Konfirmasi alamat dan koordinat tetap server-authoritative.</small>
    </section>
  );
}
